import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import apiService from '../services/api';

type EmailTemplate = {
  id: string;
  label: string;
  description: string;
};

const EmailTestsSection: React.FC = () => {
  const [defaultRecipient, setDefaultRecipient] = useState('odoreille@gmail.com');
  const [recipient, setRecipient] = useState('odoreille@gmail.com');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await apiService.getEmailTestTemplates();
      setDefaultRecipient(data.default_recipient);
      setRecipient((current) => current.trim() || data.default_recipient);
      setTemplates(data.templates);
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'Impossible de charger les modèles d’email');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const sendTest = async (templateId: string) => {
    setSendingId(templateId);
    setSuccessMessage(null);
    setActionError(null);
    try {
      const result = await apiService.sendEmailTest(templateId, recipient.trim() || defaultRecipient);
      setSuccessMessage(result.message);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setActionError(msg || (e instanceof Error ? e.message : 'Échec de l’envoi'));
    } finally {
      setSendingId(null);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EmailIcon color="primary" />
        Test des emails
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Envoyez chaque modèle d’email transactionnel avec des données de démonstration. Les messages partent
        réellement via le serveur SMTP configuré. Préférez une adresse externe (Gmail, etc.) : les tests vers
        une boîte @sherlook.fr depuis no-reply@sherlook.fr sont souvent filtrés côté OVH.
      </Alert>

      <TextField
        fullWidth
        label="Destinataire"
        type="email"
        value={recipient}
        onChange={(e) => setRecipient(e.target.value)}
        helperText={`Par défaut : ${defaultRecipient}`}
        sx={{ mb: 2, maxWidth: 420 }}
        size="small"
      />

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {templates.map((template) => (
            <Card key={template.id} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <ListItem disablePadding>
                  <ListItemText
                    primary={template.label}
                    secondary={template.description}
                    primaryTypographyProps={{ fontWeight: 700 }}
                  />
                </ListItem>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  size="small"
                  disabled={sendingId !== null}
                  onClick={() => void sendTest(template.id)}
                  startIcon={
                    sendingId === template.id ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />
                  }
                >
                  {sendingId === template.id ? 'Envoi…' : 'Envoyer'}
                </Button>
              </CardActions>
            </Card>
          ))}
        </List>
      )}
    </Box>
  );
};

export default EmailTestsSection;
